import {
    Alert,
    Box,
    Card,
    CardActionArea, CardActions, CardContent,
    Chip,
    CircularProgress,
    Divider,
    Grid,
    Grow, IconButton, InputAdornment, ListItemIcon, Menu, MenuItem, Pagination,
    Paper, Stack, TextField, Tooltip,
    Typography
} from "@mui/material";
import Button from "@mui/material/Button";
import {
    AddOutlined,
    BoltOutlined,
    CheckCircle,
    DeleteOutlined,
    DownloadOutlined,
    EditOutlined,
    HealthAndSafetyOutlined,
    InsightsOutlined,
    MoreVertOutlined, ReportProblem, SearchOutlined
} from "@mui/icons-material";
import {green, grey, lightBlue, orange} from "@mui/material/colors";
import {alpha} from "@mui/material/styles";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useHistory} from "react-router";
import {PathGenerator, PathsMap} from "../app/PageRouting";
import {httpClient} from "../utils/httpClient";
import AppConsts from "../app/AppConsts";
import {logError} from "../utils/logging";
import {useSnackbar} from "../providers/SnackbarProvider";
import {t} from "i18next";
import {scrollToElementByRef} from "../utils/scroll";
import {NavLink} from "react-router-dom";
import {useLocalizationProvider} from "../hooks/useLocalizationProvider";
import {useUser} from "../user-auth/UserProvider";
import {STEP_STATES, STEPS, STRUCTURE_TYPE_LABELS} from "./PreHospitalWorkflowConfig";
import {DialogWrapper} from "../components/DialogWrapper";
import {ImportPreHospitalWorkflowDialog} from "./ImportPreHospitalWorkflowDialog";

export function PreHospitalWorkflowsTab() {
    const [viewState, setViewState] = useState({code: "loading"});
    const [pageNumber, setPageNumber] = useState(1);
    const [workflows, setWorkflows] = useState(null);
    const history = useHistory();
    const {createSnackbar} = useSnackbar();
    const topRef = useRef(null);
    const [refreshCounter, setRefreshCounter] = useState(0);
    const user = useUser();
    const [searchInput, setSearchInput] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const searchDebounceTimeout = useRef(null);

    const handleSearchQueryChange = useCallback((value) => {
        setSearchInput(value);
        clearTimeout(searchDebounceTimeout.current);
        searchDebounceTimeout.current = setTimeout(() => {
            setDebouncedSearch(value.trim());
            setPageNumber(1);
        }, 350);
    }, []);

    useEffect(() => {
        return () => clearTimeout(searchDebounceTimeout.current);
    }, []);

    const handleSSOLogin = () => {
        if (process.env.REACT_APP_OAUTH_MODE === 'public') {
            history.push(PathsMap.OAUTH_PUBLIC_CLIENT);
        }
    };

    const handleNewClick = () => {
        httpClient.post(`${AppConsts.API_PATH}/pre-hospital-workflows`, {
            patient: {},
            caregiverContact: {},
            anamnesis: {},
            parameters: {}
        }).then(response => {
            if (response.data.workflow) {
                history.push(PathGenerator.preHospitalWorkflow(response.data.workflow.id));
            } else {
                createSnackbar({
                    message: response.data.errors[0].message,
                    autoHideDuration: 2000
                });
            }
        }).catch((e) => {
            logError(e);
            createSnackbar({
                message: t('common.sorry_something_went_wrong'),
                autoHideDuration: 2000
            });
        });
    };

    const handleReloadPage = () => {
        setRefreshCounter(counter => counter + 1);
    };

    const handleImported = useCallback((workflowId) => {
        history.push(PathGenerator.preHospitalWorkflow(workflowId));
    }, [history]);

    const [importDialog, openImportDialog] = DialogWrapper({
        component: ImportPreHospitalWorkflowDialog,
        componentProps: {onImported: handleImported, onRequireSSOLogin: handleSSOLogin}
    });

    useEffect(() => {
        const cancelTokenSource = httpClient.cancelToken();
        let isMounted = true;

        httpClient.get(`${AppConsts.API_PATH}/pre-hospital-workflows`, {
            params: {
                pageNumber,
                pageSize: 12,
                patientQuery: debouncedSearch || undefined
            },
            cancelToken: cancelTokenSource.token
        }).then(response => {
            if (!isMounted) {
                return;
            }
            if (response.data.errors) {
                setViewState({
                    code: "loadingError",
                    text: response.data.errors[0].message
                });
            } else {
                setWorkflows({
                    pageNumber: response.data.pageable.pageNumber + 1,
                    totalPages: response.data.totalPages,
                    totalForms: response.data.totalElements,
                    list: response.data.content
                });
                setViewState({code: "loaded"});
            }
        }).catch((e) => {
            logError(e);
            if (!isMounted) {
                return;
            }
            setViewState({
                code: "loadingError",
                text: t('common.sorry_something_went_wrong')
            });
        });

        return () => {
            isMounted = false;
            cancelTokenSource.cancel();
        }
    }, [pageNumber, refreshCounter, user.data.isSSO, debouncedSearch]);

    if (viewState.code === "loading") {
        return <CircularProgress/>;
    }

    if (viewState.code === "loadingError") {
        return <Alert severity="error">{viewState.text}</Alert>;
    }

    const hasActiveSearch = debouncedSearch !== "";

    return (
        <Box ref={topRef}>
            <PreHospitalWorkflowsToolbar
                onNewClick={handleNewClick}
                onImportClick={openImportDialog}
                showImport={user.data.isSSO}
                searchQuery={searchInput}
                onSearchQueryChange={handleSearchQueryChange}
            />
            {importDialog}
            {workflows.list.length === 0 && !hasActiveSearch &&
                <Paper variant="outlined" sx={{
                    flexGrow: true,
                    display: 'flex',
                    alignItems: ' center',
                    minHeight: "40vh",
                    justifyContent: "center"
                }}>
                    <Stack direction="row" spacing={2}>
                        <Button variant="outlined" startIcon={<AddOutlined/>} onClick={handleNewClick}>
                            Nuovo Triage
                        </Button>
                        {user.data.isSSO &&
                            <Button variant="outlined" startIcon={<DownloadOutlined/>} onClick={openImportDialog}>
                                Importa triage
                            </Button>}
                    </Stack>
                </Paper>
            }
            {workflows.list.length === 0 && hasActiveSearch &&
                <Paper variant="outlined" sx={{
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: "40vh",
                    justifyContent: "center"
                }}>
                    <Typography color="text.secondary">
                        Nessun triage trovato per "{searchInput}".
                    </Typography>
                </Paper>
            }
            <Grid container spacing={4}>
                {workflows.list.map(m => (
                    <Grid item xs={12} sm={6} lg={4} key={m.id}>
                        <PreHospitalWorkflowListElem workflow={m} handleReloadPage={handleReloadPage} />
                    </Grid>
                ))}
            </Grid>
            {workflows.totalPages > 1 && <>
                <Divider sx={{mt: 5, mb: 2}}/>
                <Pagination
                    onChange={(e, page) => {
                        setPageNumber(page);
                        scrollToElementByRef(topRef);
                    }}
                    count={workflows.totalPages}/></>}
        </Box>
    );
}

function PreHospitalWorkflowsToolbar({onNewClick, onImportClick, showImport, searchQuery, onSearchQueryChange}) {
    return (
        <Stack direction={{xs: "column", md: "row"}} spacing={2} sx={{mb: 4}} alignItems={{xs: "stretch", md: "center"}}>
            <Button variant="contained" startIcon={<AddOutlined/>} onClick={onNewClick}>
                Nuovo Triage
            </Button>
            {showImport &&
                <Button variant="outlined" startIcon={<DownloadOutlined/>} onClick={onImportClick}>
                    Importa triage
                </Button>}
            <TextField
                size="small"
                placeholder="Cerca per nome o codice fiscale"
                value={searchQuery}
                onChange={(e) => onSearchQueryChange(e.target.value)}
                sx={{ml: {md: 'auto'}, width: {xs: '100%', md: 320}}}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <SearchOutlined/>
                        </InputAdornment>
                    )
                }}
            />
        </Stack>
    );
}

function WorkflowStepsIndicator({steps}) {
    if (!steps) {
        return null;
    }

    return (
        <Stack direction="row" spacing={1} sx={{mt: 2}}>
            {STEPS.map((step) => {
                const state = steps[step.id];
                let icon = <CheckCircle sx={{fontSize: 20}} color="disabled"/>;
                let statusLabel = "Opzionale";

                if (state === STEP_STATES.COMPLETED) {
                    icon = <CheckCircle sx={{fontSize: 20}} color="success"/>;
                    statusLabel = "Completato";
                } else if (state === STEP_STATES.INCOMPLETE_REQUIRED) {
                    icon = <ReportProblem sx={{fontSize: 20}} color="error"/>;
                    statusLabel = "Da completare";
                }

                return (
                    <Tooltip key={step.id} title={`${step.label}: ${statusLabel}`}>
                        {icon}
                    </Tooltip>
                );
            })}
        </Stack>
    );
}

function WorkflowOutputSummaryRow({icon, label, chip}) {
    return (
        <Stack direction="row" spacing={1} alignItems="center">
            {icon}
            <Typography variant="body2" color="text.secondary" sx={{flexGrow: 1}} noWrap>
                {label}
            </Typography>
            {chip}
        </Stack>
    );
}

function UnknownValueChip() {
    return (
        <Chip
            size="small"
            label="?"
            sx={{bgcolor: grey[300], color: grey[700], fontWeight: 700, minWidth: 32}}/>
    );
}

function WorkflowOutputSummary({output}) {
    const hasResults = Boolean(output.generatedAt);

    return (
        <Box sx={{position: 'relative', mt: 2}}>
            <Stack spacing={1} sx={!hasResults ? {filter: 'blur(2px)', opacity: 0.6} : undefined}>
                <WorkflowOutputSummaryRow
                    icon={<BoltOutlined sx={{fontSize: 18, color: green[600]}}/>}
                    label="Codice stroke"
                    chip={output.strokeCodeActivation.value === null
                        ? <UnknownValueChip/>
                        : <Chip size="small"
                                color={output.strokeCodeActivation.value ? "success" : "error"}
                                label={output.strokeCodeActivation.value ? "Sì" : "No"}/>}/>
                <WorkflowOutputSummaryRow
                    icon={<InsightsOutlined sx={{fontSize: 18, color: orange[700]}}/>}
                    label="Probabilità LVO"
                    chip={output.lvoProbability.value === null
                        ? <UnknownValueChip/>
                        : <Chip size="small"
                                color={output.lvoProbability.value > 50 ? "error" : "warning"}
                                label={output.lvoProbability.value + "%"}/>}/>
                <WorkflowOutputSummaryRow
                    icon={<HealthAndSafetyOutlined sx={{fontSize: 18, color: lightBlue[700]}}/>}
                    label="Struttura consigliata"
                    chip={output.structureType.value === null
                        ? <UnknownValueChip/>
                        : <Chip size="small" color="info"
                                label={STRUCTURE_TYPE_LABELS[output.structureType.value] ?? output.structureType.value}/>}/>
            </Stack>
            {!hasResults &&
                <Box sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    p: 1,
                    borderRadius: 1,
                    backdropFilter: 'blur(3px)',
                    bgcolor: (theme) => alpha(theme.palette.background.paper, 0.6)
                }}>
                    <Typography variant="body2" sx={{fontWeight: 600}}>
                        Nessun risultato generato, completa gli step obbligatori.
                    </Typography>
                </Box>}
        </Box>
    );
}

function PreHospitalWorkflowListElem({workflow, handleReloadPage}) {
    const history = useHistory();
    const {createSnackbar} = useSnackbar();

    const [menuAnchor, setMenuAnchor] = useState(null);
    const menuOpen = Boolean(menuAnchor);
    const handleCloseMenu = useCallback(() => {
        setMenuAnchor(null);
    }, []);
    const handleClickMore = useCallback((event) => {
        setMenuAnchor(event.currentTarget);
    }, [setMenuAnchor]);

    const handleDelete = useCallback(() => {
        handleCloseMenu();
        if (window.confirm("Confermi di voler rimuovere il triage?")) {
            httpClient.delete(`${AppConsts.API_PATH}/pre-hospital-workflows/${workflow.id}`).then(response => {
                if (response.data.id) {
                    handleReloadPage();
                } else {
                    createSnackbar({
                        message: response.data.errors[0].message,
                        autoHideDuration: 2000
                    });
                }
            }).catch(() => {
                createSnackbar({
                    message: t('common.sorry_something_went_wrong'),
                    autoHideDuration: 2000
                });
            });
        }
    }, [createSnackbar, handleCloseMenu, handleReloadPage, workflow.id]);

    const {localeBcp47} = useLocalizationProvider();

    const lastUpdateAtFormatted = useMemo(() => {
        return new Date(workflow.lastUpdateAt).toLocaleString(localeBcp47);
    }, [workflow.lastUpdateAt, localeBcp47]);

    return (
        <Grow in={true}>
            <Card variant="outlined" sx={{width: 1}}>
                <CardActionArea onClick={() => history.push(PathGenerator.preHospitalWorkflow(workflow.id))}>
                    <CardContent>
                        <Typography gutterBottom variant="h2" noWrap>
                            {workflow.patient.name || workflow.patient.fiscalCode || 'Paziente senza nome'}
                        </Typography>
                        <Typography color="text.secondary" noWrap>
                            {lastUpdateAtFormatted}
                        </Typography>
                        <WorkflowStepsIndicator steps={workflow.steps}/>
                        <Divider sx={{my: 2}}/>
                        {workflow.output && <WorkflowOutputSummary output={workflow.output}/>}
                    </CardContent>
                </CardActionArea>
                <CardActions>
                    <Stack direction="row" spacing={2} justifyContent="space-between" sx={{width: 1}}>
                        <Button component={NavLink} to={PathGenerator.preHospitalWorkflow(workflow.id)} size="small">
                            <EditOutlined sx={{fontSize: 14, mr: 1}}/>
                            Modifica
                        </Button>
                        <IconButton
                            aria-haspopup="true"
                            onClick={handleClickMore}>
                            <MoreVertOutlined/>
                        </IconButton>
                    </Stack>
                </CardActions>
                <Menu
                    anchorEl={menuAnchor}
                    open={menuOpen}
                    onClose={handleCloseMenu}
                    PaperProps={{
                        style: {
                            minWidth: 220,
                        },
                    }}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}>
                    <MenuItem onClick={handleDelete} key="delete">
                        <ListItemIcon>
                            <DeleteOutlined fontSize="small"/>
                        </ListItemIcon>
                        {t('common.delete')}
                    </MenuItem>
                </Menu>
            </Card>
        </Grow>
    );
}